import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PlanDisplay } from './PlanCard';
import TherapistPlanCard from './TherapistPlanCard';
import { groupTherapistPlans } from './groupTherapistPlans';

interface TherapistPlansLayoutProps {
  plans: PlanDisplay[];
  isLoading: boolean;
  processingPlan: string | null;
  onSelect: (id: string, quantity?: number) => void;
  showBackLink?: boolean;
}

interface PlanSlotProps {
  plan: PlanDisplay;
  processingPlan: string | null;
  onSelect: (id: string, quantity?: number) => void;
  className?: string;
  animationDelay?: string;
}

const PlanSlot: React.FC<PlanSlotProps> = ({
  plan,
  processingPlan,
  onSelect,
  className = '',
  animationDelay,
}) => (
  <div
    className={`plans-animate-in flex h-full flex-col ${className}`}
    style={animationDelay ? { animationDelay } : undefined}
  >
    <TherapistPlanCard
      plan={plan}
      onSelect={onSelect}
      isLoading={processingPlan === plan.id}
    />
  </div>
);

const TherapistPlansIntro: React.FC = () => (
  <header className="plans-animate-in plans-animate-delay-1 mx-auto mb-10 max-w-4xl text-left md:mb-14">
    <h1 className="sr-only">Planos para Consteladores</h1>
    <p className="text-base font-medium leading-relaxed text-[#4A7C75] sm:text-lg md:text-xl md:leading-loose">
      Transforme sua prática de constelação com uma plataforma desenvolvida especialmente para{' '}
      <span className="text-2xl font-bold text-[#A56096] md:text-3xl">Consteladores</span>: contrate
      horas avulsas, adquira pacotes ou escolha um plano de assinatura com homologação e destaque na
      vitrine de consteladores da plataforma, ampliando sua visibilidade e conduzindo sessões online
      com flexibilidade e a mesma conexão e profundidade vivenciadas no presencial.
    </p>
  </header>
);

const HourPlansGrid: React.FC<{
  avulso: PlanDisplay | null;
  pacotes: PlanDisplay[];
  processingPlan: string | null;
  onSelect: (id: string, quantity?: number) => void;
}> = ({ avulso, pacotes, processingPlan, onSelect }) => {
  if (!avulso && pacotes.length === 0) return null;

  const pacoteCols =
    pacotes.length === 1
      ? 'max-w-md mx-auto'
      : pacotes.length === 2
        ? 'md:grid-cols-2 max-w-4xl mx-auto'
        : 'md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto';

  return (
    <div className="space-y-6 md:space-y-8">
      {avulso && (
        <PlanSlot
          plan={avulso}
          processingPlan={processingPlan}
          onSelect={onSelect}
          className="mx-auto w-full max-w-2xl"
          animationDelay="0.15s"
        />
      )}

      {pacotes.length > 0 && (
        <div className={`grid grid-cols-1 gap-6 md:gap-8 ${pacoteCols}`}>
          {pacotes.map((plan, index) => (
            <PlanSlot
              key={plan.id}
              plan={plan}
              processingPlan={processingPlan}
              onSelect={onSelect}
              animationDelay={`${0.2 + index * 0.06}s`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SubscriptionPlansGrid: React.FC<{
  plans: PlanDisplay[];
  processingPlan: string | null;
  onSelect: (id: string, quantity?: number) => void;
  baseDelay?: number;
}> = ({ plans, processingPlan, onSelect, baseDelay = 0.25 }) => {
  if (plans.length === 0) return null;

  const cols =
    plans.length === 1
      ? 'max-w-md mx-auto'
      : plans.length === 2
        ? 'md:grid-cols-2 max-w-4xl mx-auto'
        : 'md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto';

  return (
    <div className={`grid grid-cols-1 gap-6 md:gap-8 ${cols}`}>
      {plans.map((plan, index) => (
        <PlanSlot
          key={plan.id}
          plan={plan}
          processingPlan={processingPlan}
          onSelect={onSelect}
          animationDelay={`${baseDelay + index * 0.06}s`}
        />
      ))}
    </div>
  );
};

const TherapistPlansLayout: React.FC<TherapistPlansLayoutProps> = ({
  plans,
  isLoading,
  processingPlan,
  onSelect,
  showBackLink = true,
}) => {
  const { avulso, pacotes, subscriptions } = groupTherapistPlans(plans);
  const hasHourSection = !!avulso || pacotes.length > 0;
  const hasSubscriptionSection = subscriptions.length > 0;

  return (
    <div className="mx-auto max-w-6xl px-1 sm:px-2">
      {showBackLink && (
        <Link
          to="/planos"
          className="plans-animate-in mb-8 inline-flex items-center gap-2 rounded-full border border-[#4A7C75]/20 bg-white/80 px-4 py-2 text-sm font-medium text-[#4A7C75] shadow-sm backdrop-blur-sm transition hover:border-[#4A7C75]/40 hover:bg-white hover:shadow-md"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Voltar para escolha de caminho
        </Link>
      )}

      <TherapistPlansIntro />

      <section className="plans-animate-in plans-animate-delay-2" aria-label="Planos disponíveis">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div
              className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-[#7ED99C] border-r-transparent"
              role="status"
              aria-label="Carregando planos"
            />
            <p className="mt-4 font-medium text-[#4A7C75]">Carregando planos...</p>
          </div>
        ) : plans.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-[#4A7C75]/30 bg-white py-16 text-center shadow-sm ring-1 ring-black/5">
            <p className="text-[#6C4E78]">Nenhum plano disponível no momento.</p>
          </div>
        ) : (
          <div className="space-y-10 md:space-y-12">
            {hasHourSection && (
              <HourPlansGrid
                avulso={avulso}
                pacotes={pacotes}
                processingPlan={processingPlan}
                onSelect={onSelect}
              />
            )}

            {hasSubscriptionSection && (
              <SubscriptionPlansGrid
                plans={subscriptions}
                processingPlan={processingPlan}
                onSelect={onSelect}
              />
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default TherapistPlansLayout;
